import { Form, Formik, FormikProps } from 'formik';
import { FormOfAddress } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolModal } from '@synergy/components';
import { useSearchParams } from 'react-router-dom';

import { Contact } from '../../common/models/contact';
import { ContactRequest } from './models/contact-request';
import { ContactsLoginsDetailForm } from './contacts-logins-detail-form';
import { ContactsLoginsFormikValues } from './models/contacts-logins-formik-values';
import { LoginUser, isLoginUser } from './models/login-user';
import { contactsLoginsValidationSchema } from './contacts-logins-validation-schema';
import { createUser, fetchUsers, patchUser } from '../../common/actions/contacts-logins-actions';
import { useThunkDispatch } from '../../store/use-thunk-dispatch';

interface ContactsLoginsDetailModalProps {
    visible: boolean;
    user?: Contact | LoginUser;
    onClose: () => void;
}

export const ContactsLoginsDetailModal = (props: ContactsLoginsDetailModalProps) => {
    const dispatch = useThunkDispatch();
    const [searchParams] = useSearchParams();

    if (!props.visible) {
        return null;
    }

    const getInitialValues = (initialUser?: Contact | LoginUser): ContactsLoginsFormikValues => {
        const loginUser = isLoginUser(initialUser) ? initialUser : undefined;

        return {
            id: initialUser?.id,
            firstName: initialUser?.firstName ?? '',
            lastName: initialUser?.lastName ?? '',
            positionId: initialUser?.positionId ?? null,
            email: initialUser?.email ?? '',
            phone: initialUser?.phone ?? '',
            formOfAddress: initialUser?.formOfAddress ?? FormOfAddress.Mr,
            perOrderOrderCeiling: loginUser?.perOrderOrderCeiling,
            monthlyOrderCeiling: loginUser?.monthlyOrderCeiling ?? -1,
            isUser: !!loginUser,
        };
    };

    const handleSubmit = async (values: ContactsLoginsFormikValues) => {
        const newUser = getUserRequestFromValues(values);
        if (!props.user) {
            if (await dispatch(createUser(newUser))) {
                props.onClose();
                await dispatch(fetchUsers(searchParams));
            }
        } else {
            if (await dispatch(patchUser(newUser))) {
                props.onClose();
                await dispatch(fetchUsers(searchParams));
            }
        }
    };

    const getUserRequestFromValues = (formValues: ContactsLoginsFormikValues): ContactRequest => ({
        id: props.user?.id ?? -1,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        positionId: formValues.positionId,
        email: formValues.email,
        phone: formValues.phone || null,
        formOfAddress: formValues.formOfAddress,
        enableLogin: formValues.isUser ? true : undefined,

        ...(formValues.isUser && {
            monthlyOrderCeiling:
                formValues.monthlyOrderCeiling === -1 || formValues.monthlyOrderCeiling === null
                    ? null
                    : +formValues.monthlyOrderCeiling,
            perOrderOrderCeiling: formValues.perOrderOrderCeiling || null, // Used to send null for an empty string value
        }),
    });

    const actionChildren = (formikProps: FormikProps<ContactsLoginsFormikValues>) => (
        <>
            <PoolButton key="2" styleType="firstLevel" variant="text" onClick={() => props.onClose()}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>
            <PoolButton key="3" styleType="firstLevel" type="submit" onClick={() => formikProps.submitForm()}>
                <FormattedMessage id="app.common.save" />
            </PoolButton>
        </>
    );

    return (
        <Formik
            initialValues={getInitialValues(props.user)}
            onSubmit={handleSubmit}
            validationSchema={contactsLoginsValidationSchema}
        >
            {(formikProps: FormikProps<ContactsLoginsFormikValues>) => (
                <Form onSubmit={formikProps.handleSubmit}>
                    <PoolModal
                        handleClose={() => props.onClose()}
                        isVisible={props.visible}
                        actionChildren={actionChildren(formikProps)}
                        title={
                            <FormattedMessage
                                id={props.user ? 'app.contacts.logins.change' : 'app.contacts.logins.addContactLogin'}
                            />
                        }
                    >
                        <ContactsLoginsDetailForm />
                    </PoolModal>
                </Form>
            )}
        </Formik>
    );
};
